import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "../../../utils/apiConfig";
import { useB2BTranslation } from "../B2BShared/hooks/useB2BTranslation";
import { useB2BDataLoadingState } from "../B2BShared/hooks/useB2BLoadingState";
import { B2B_INTERESTED_IN_OPTIONS } from "../B2BShared/constants";
import {
  AlertCircle,
  X,
  ChevronLeft,
  ChevronRight,
  Loader2,
} from "lucide-react";
import B2BParticipantsCard from "../B2BParticipantsCard/B2BParticipantsCard";
import B2BBackButton from "../B2BShared/B2BBackButton";
import B2BProfileCompletionAlert from "../B2BShared/B2BProfileCompletionAlert/B2BProfileCompletionAlert";
import { useB2BAuth } from "../B2BShared/B2BAuthContext/B2BAuthContext";
import { GlassButton } from "../B2BShared/GlassButton/GlassButton";

import "./B2BParticipantsList.css";

interface Participant {
  _id?: string;
  id?: string;
  name?: string;
  surname?: string;
  fullName?: string;
  email?: string;
  phoneNumber?: string;
  phonePrivate?: string;
  position?: string | string[];
  companyName?: string;
  website?: string;
  avatar?: string;
  countriesOperating?: string[] | string;
  cityHQ?: string;
  [key: string]: any; // Allow additional properties
}

interface QueryParams {
  page: number;
  limit: number;
  search?: string;
  category?: string;
  location?: string;
  interests?: string[];
}

interface Filters {
  search?: string;
  category?: string;
  location?: string;
  interests?: string[];
}


// Limited list of countries matching the profile creation form
const PROFILE_COUNTRIES = [
  "worldwide",
  "unitedKingdom",
  "unitedStates",
  "france",
  "germany",
  "italy",
  "spain",
  "netherlands",
  "switzerland",
  "canada",
  "australia",
  "japan",
  "singapore",
  "unitedArabEmirates",
  "russia",
  "china",
  "india",
  "brazil",
  "mexico",
  "other"
];

// Business categories matching the profile creation form
const BUSINESS_CATEGORIES = [
  "accommodation",
  "artAndCulture",
  "beauty",
  "concierge",
  "consulting",
  "delivery",
  "education",
  "entertainment",
  "finance",
  "foodAndBeverages",
  "health",
  "immigration",
  "insurance",
  "it",
  "legal",
  "realEstate",
  "recruitment",
  "executiveRecruitment",
  "restaurants",
  "services",
  "tax",
  "transport",
  "travel",
  "wellness",
  "other"
];

const B2BParticipantsList: React.FC = () => {
  const { t, tParticipantsRoot, tAuth, tBusinessCategory } = useB2BTranslation();
  const { user } = useB2BAuth();

  // Data state
  const [participants, setParticipants] = useState<Participant[]>([]);
  
  const {
    isLoading,
    error,
    fetchData
  } = useB2BDataLoadingState();

  const PAGE_SIZE = 50;

  // Pagination state
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalParticipants, setTotalParticipants] = useState<number>(0);
  const [hasNextPage, setHasNextPage] = useState<boolean>(false);
  const [hasPrevPage, setHasPrevPage] = useState<boolean>(false);

  // Filter state
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedCountry, setSelectedCountry] = useState<string>("");
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [selectedInterest, setSelectedInterest] = useState<string>("");

  // Fetch participants with proper backend integration
  const fetchParticipants = useCallback(
    async (page: number = 1, filters: Filters = {}): Promise<void> => {
      await fetchData(async () => {
        // Build query parameters - only include non-empty values
        const queryParams: QueryParams = {
          page,
          limit: PAGE_SIZE,
        };

        // Only add search if it meets backend validation requirements (2+ characters)
        const searchQuery = filters.search || searchTerm;
        if (searchQuery && searchQuery.trim().length >= 2) {
          queryParams.search = searchQuery.trim();
        }

        // Only add category if it's not empty
        const categoryQuery = filters.category || selectedCategory;
        if (categoryQuery && categoryQuery.trim()) {
          queryParams.category = categoryQuery.trim();
        }

        // Only add location if it's not empty
        const locationQuery = filters.location || selectedCountry;
        if (locationQuery && locationQuery.trim()) {
          queryParams.location = locationQuery.trim();
        }

        // Only add interests if it's not empty
        if (selectedInterest) {
          queryParams.interests = [selectedInterest];
        } else if (
          filters.interests &&
          Array.isArray(filters.interests) &&
          filters.interests.length > 0
        ) {
          queryParams.interests = filters.interests;
        }

        const response = await api.getParticipants(queryParams);

        if (response.success && response.data) {
          const { participants: participantsData, pagination } = response.data;

          setParticipants(participantsData);
          setCurrentPage(pagination.page);
          setTotalPages(pagination.pages);
          setTotalParticipants(pagination.total);
          setHasNextPage(pagination.hasNext);
          setHasPrevPage(pagination.hasPrev);
        } else {
          throw new Error(response.message || "Failed to fetch participants");
        }
      }).catch((error) => {
        setParticipants([]);
      });
    },
    [searchTerm, selectedCategory, selectedCountry, selectedInterest, fetchData]
  );

  // Initial load
  useEffect(() => {
    fetchParticipants(1);
  }, [fetchParticipants]);

  // Refetch when filters change
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchParticipants(1, {
        search: searchTerm,
        category: selectedCategory,
        location: selectedCountry,
        interests: selectedInterest ? [selectedInterest] : [],
      });
    }, 300); // Debounce search

    return () => clearTimeout(timeoutId);
  }, [
    searchTerm,
    selectedCategory,
    selectedCountry,
    selectedInterest,
    fetchParticipants,
  ]);

  // Pagination handlers
  const handlePageChange = (page: number): void => {
    setCurrentPage(page);
    fetchParticipants(page, {
      search: searchTerm,
      category: selectedCategory,
      location: selectedCountry,
      interests: selectedInterest ? [selectedInterest] : [],
    });
  };

  const handleNextPage = (): void => {
    if (hasNextPage) {
      handlePageChange(currentPage + 1);
    }
  };

  const handlePrevPage = (): void => {
    if (hasPrevPage) {
      handlePageChange(currentPage - 1);
    }
  };

  // Filter handlers
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setSearchTerm(e.target.value);
  };

  const handleCountryChange = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    setSelectedCountry(e.target.value);
  };

  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    setSelectedCategory(e.target.value);
  };

  const handleInterestChange = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    setSelectedInterest(e.target.value);
  };

  const clearAllFilters = (): void => {
    setSearchTerm("");
    setSelectedCountry("");
    setSelectedCategory("");
    setSelectedInterest("");
    fetchParticipants(1, {});
  };

  // Error retry handler
  const handleRetry = (): void => {
    fetchParticipants(currentPage, {
      search: searchTerm,
      category: selectedCategory,
      location: selectedCountry,
      interests: selectedInterest ? [selectedInterest] : [],
    });
  };

  // Loading state
  if (isLoading && participants.length === 0) {
    return (
      <div className="b2b-participants-container">
        <div className="b2b-loading">
          <Loader2 className="b2b-loading-spinner" />
          <p>{tParticipantsRoot("participants_loading")}</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error && participants.length === 0) {
    return (
      <div className="b2b-participants-container">
        <B2BBackButton
          to="/gcb/dashboard"
          translationKey="participants_backToDashboard"
          variant="orange"
          size="medium"
          namespace=""
        />
        <div className="b2b-error">
          <AlertCircle className="b2b-error-icon" />
          <h3>{tParticipantsRoot("participants_errorTitle")}</h3>
          <p>
            {error.includes("Validation failed")
              ? "The request could not be processed. The limit might be too high or there's a validation error with the filters."
              : error.includes("Token expired")
                ? "Your session has expired. Please log in again."
                : error}
          </p>
          <div className="b2b-error-actions">
            <GlassButton
              onClick={handleRetry}
              variant="primary"
              size="medium"
            >
              {tParticipantsRoot("participants_retry")}
            </GlassButton>
            <Link to="/gcb/dashboard">
              <GlassButton
                variant="secondary"
                size="medium"
              >
                {tParticipantsRoot("participants_backToDashboard")}
              </GlassButton>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="b2b-participants-container">
      {/* Header */}
      <div className="b2b-participants-header">
        <B2BBackButton
          to="/gcb/dashboard"
          translationKey="participants_backToDashboard"
          variant="orange"
          size="medium"
          namespace=""
        />
        <h1>{tParticipantsRoot("participants_title")}</h1>
      </div>

      {/* Profile Completion Alert */}
      <B2BProfileCompletionAlert />

      {/* Filters */}
      <div className="b2b-filters-section">
        <div className="b2b-filters-row">
          <div className="b2b-search-container">
            <input
              type="text"
              placeholder={tParticipantsRoot("participants_searchPlaceholder")}
              value={searchTerm}
              onChange={handleSearchChange}
              className="b2b-search-input"
            />
          </div>

          <div className="b2b-filter-dropdowns">
            <select
              value={selectedCountry}
              onChange={handleCountryChange}
              className="b2b-filter-select"
            >
              <option value="">
                {tParticipantsRoot("participants_filters_allCountries")}
              </option>
              {PROFILE_COUNTRIES.map((countryCode) => {
                // Get translation for the country using the dashboard.profile.countries namespace
                const countryKey = `dashboard.profile.countries.${countryCode}`;
                let countryName = t(countryKey);
                
                // If translation doesn't exist, try lowercase version
                if (countryName === countryKey) {
                  const lowerKey = `dashboard.profile.countries.${countryCode.toLowerCase()}`;
                  const lowerName = t(lowerKey);
                  if (lowerName !== lowerKey) {
                    countryName = lowerName;
                  }
                }
                
                // Use the translated name, or fallback to formatted country code
                const displayName = countryName !== countryKey && countryName !== `dashboard.profile.countries.${countryCode.toLowerCase()}`
                  ? countryName 
                  : countryCode
                    .replace(/([A-Z])/g, ' $1')
                    .replace(/^./, str => str.toUpperCase())
                    .trim();
                
                return (
                  <option
                    key={countryCode}
                    value={countryCode}
                  >
                    {displayName}
                  </option>
                );
              })}
            </select>

            <select
              value={selectedCategory}
              onChange={handleCategoryChange}
              className="b2b-filter-select"
            >
              <option value="">
                {tParticipantsRoot("participants_filters_allCategories")}
              </option>
              {BUSINESS_CATEGORIES.map((category) => (
                <option key={category} value={category}>
                  {tBusinessCategory(category)}
                </option>
              ))}
            </select>

            <select
              value={selectedInterest}
              onChange={handleInterestChange}
              className="b2b-filter-select"
            >
              <option value="">
                {tParticipantsRoot("participants_filters_allInterests")}
              </option>
              {B2B_INTERESTED_IN_OPTIONS.map((interest) => (
                <option
                  key={interest}
                  value={interest}
                >
                  {tAuth(`interestedInOptions.${interest}`)}
                </option>
              ))}
            </select>
          </div>

          <div className="b2b-filter-actions">
            <GlassButton
              onClick={clearAllFilters}
              variant="neutral"
              size="medium"
            >
              <X className="b2b-clear-icon" />
              {tParticipantsRoot("participants_clearAllFilters")}
            </GlassButton>
          </div>
        </div>
      </div>

      {/* Results count and pagination info */}
      <div className="b2b-results-info">
        <div className="b2b-results-count">
          <p>
            {(() => {
              const start = (currentPage - 1) * PAGE_SIZE + 1;
              const end = (currentPage - 1) * PAGE_SIZE + participants.length;
              return `${start}–${end} ${tParticipantsRoot("participants_of")} ${totalParticipants}`;
            })()}
          </p>
          {isLoading && (
            <div className="b2b-loading-indicator">
              <Loader2 className="b2b-loading-spinner-small" />
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="b2b-pagination">
            <GlassButton
              onClick={handlePrevPage}
              disabled={!hasPrevPage || isLoading}
              variant="secondary"
              size="medium"
              className="b2b-pagination-btn"
            >
              <ChevronLeft />
              {tParticipantsRoot("participants_previous")}
            </GlassButton>

            <div className="b2b-pagination-info">
              {tParticipantsRoot("page")} {currentPage}{" "}
              {tParticipantsRoot("participants_of")} {totalPages}
            </div>

            <GlassButton
              onClick={handleNextPage}
              disabled={!hasNextPage || isLoading}
              variant="primary"
              size="medium"
              className="b2b-pagination-btn"
            >
              {tParticipantsRoot("participants_next")}
              <ChevronRight />
            </GlassButton>
          </div>
        )}
      </div>

      {/* Participants Grid/List */}
      <div className="b2b-participants-content">
        {participants.length === 0 && !isLoading ? (
          <div className="b2b-no-results">
            <AlertCircle className="b2b-no-results-icon" />
            <h3>{t("common.noResults")}</h3>
            <p>{t("common.noResultsDescription")}</p>
            <GlassButton
              onClick={clearAllFilters}
              variant="neutral"
              size="medium"
            >
              {tParticipantsRoot("participants_clearFilters")}
            </GlassButton>
          </div>
        ) : (
          participants.map((participant) => {
            // Transform participant data to match B2BParticipantsCard interface
            // Handle cases where API returns fullName instead of name/surname
            let participantName = participant.name || "";
            let participantSurname = participant.surname || "";
            
            if (!participantName && !participantSurname && participant.fullName) {
              const nameParts = participant.fullName.split(" ");
              participantName = nameParts[0] || "";
              participantSurname = nameParts.slice(1).join(" ") || "";
            }
            
            const transformedParticipant = {
              ...participant,
              name: participantName || "Unknown",
              surname: participantSurname || "",
              email: participant.email || "",
              position: participant.position || [],
            };
            
            return (
              <div key={participant._id || participant.id}>
                <B2BParticipantsCard
                  participant={transformedParticipant}
                  currentUser={user}
                  isOwnProfile={false}
                />
              </div>
            );
          })
        )}
      </div>

    </div>
  );
};

export default B2BParticipantsList;

